import { Fragment, type ReactNode } from "react";

/**
 * Tiny markdown renderer that's enough for the formatting actions we ship:
 *   inline: **bold**, *italic*, <u>underline</u>, ~~strike~~, `code`,
 *           ==highlight==, [text](url), bare URLs, www.urls
 *   block:  # h1 / ## h2 / ### h3
 *           > blockquote
 *           - bullet / * bullet
 *           1. numbered
 *           - [ ] / - [x] checkboxes
 *
 * Designed for short text (a card body), not full CommonMark. Inline parsing
 * is regex-based with recursion into matched content so emphasis nests
 * correctly (e.g. `**bold *and italic***`).
 */

// Pattern (not a compiled regex). Each call to `parseInline` constructs a
// fresh `RegExp` so its `lastIndex` state is isolated to that one call —
// otherwise recursion (e.g. rendering bold content inside an outer match)
// would reset the shared `lastIndex` and cause the parent loop to re-find
// the same match forever. That was the freeze bug.
// Ordered so longer delimiters (**, ~~, ==, <u>) match before shorter ones
// (*, `) to prevent premature single-* matches inside **bold**.
const INLINE_PATTERN =
  "(\\*\\*[^*\\n]+?\\*\\*|<u>[^<\\n]+?<\\/u>|~~[^~\\n]+?~~|==[^=\\n]+?==|\\*[^*\\n]+?\\*|`[^`\\n]+?`|\\[[^\\]\\n]+?\\]\\([^)\\n]+?\\)|\\b(?:https?:\\/\\/|www\\.)[^\\s<>\"]+)";

function parseInline(text: string): ReactNode[] {
  if (!text) return [];
  const re = new RegExp(INLINE_PATTERN, "g");
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  // Defensive iteration cap: even with the local regex above, a future regex
  // edit could in theory produce zero-width matches. Bail out before the
  // page locks up.
  let iter = 0;
  while ((m = re.exec(text)) !== null) {
    if (++iter > 5000) break;
    // Zero-width match would never advance lastIndex; force progress.
    if (m[0].length === 0) {
      re.lastIndex++;
      continue;
    }
    const start = m.index;
    const matched = m[0];
    const end = start + matched.length;
    if (start > last) out.push(<Fragment key={key++}>{text.slice(last, start)}</Fragment>);

    if (matched.startsWith("**")) {
      out.push(<strong key={key++}>{parseInline(matched.slice(2, -2))}</strong>);
    } else if (matched.startsWith("<u>")) {
      out.push(<u key={key++}>{parseInline(matched.slice(3, -4))}</u>);
    } else if (matched.startsWith("~~")) {
      out.push(<s key={key++}>{parseInline(matched.slice(2, -2))}</s>);
    } else if (matched.startsWith("==")) {
      out.push(
        <mark
          key={key++}
          className="rounded bg-butter-100 px-0.5 text-ink"
        >
          {parseInline(matched.slice(2, -2))}
        </mark>
      );
    } else if (matched.startsWith("*")) {
      out.push(<em key={key++}>{parseInline(matched.slice(1, -1))}</em>);
    } else if (matched.startsWith("`")) {
      out.push(
        <code
          key={key++}
          className="rounded border border-border bg-canvas px-1 py-[1px] font-mono text-[0.85em]"
        >
          {matched.slice(1, -1)}
        </code>
      );
    } else if (matched.startsWith("[")) {
      const lm = matched.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        out.push(
          <a
            key={key++}
            href={lm[2]}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="break-all text-lavender-400 underline decoration-lavender-200 decoration-1 underline-offset-2 transition-colors hover:decoration-lavender-400"
          >
            {parseInline(lm[1])}
          </a>
        );
      } else {
        out.push(matched);
      }
    } else {
      // Bare URL
      const href = matched.startsWith("http") ? matched : `https://${matched}`;
      out.push(
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="break-all text-lavender-400 underline decoration-lavender-200 decoration-1 underline-offset-2 transition-colors hover:decoration-lavender-400"
        >
          {matched}
        </a>
      );
    }
    last = end;
  }
  if (last < text.length) out.push(<Fragment key={key++}>{text.slice(last)}</Fragment>);
  return out;
}

// Block-level parser. Walks lines, groups runs of list items / quotes, emits
// React block elements. Indent (two leading spaces) creates a nested level.
export function renderMarkdown(text: string): ReactNode {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  const isBullet = (l: string) => /^\s*[-*] (?!\[[ xX]\] )/.test(l);
  const isCheck = (l: string) => /^\s*- \[[ xX]\] /.test(l);
  const isNumbered = (l: string) => /^\s*\d+\. /.test(l);
  const indentOf = (l: string) => (l.match(/^( *)/)?.[1].length ?? 0);

  while (i < lines.length) {
    const line = lines[i];

    // Headings
    const h = line.match(/^(#{1,3}) (.*)$/);
    if (h) {
      const level = h[1].length;
      const inner = h[2];
      const contents = parseInline(inner);
      if (level === 1) {
        blocks.push(
          <h1 key={key++} className="mt-2 mb-1 text-[1.35em] font-semibold leading-tight">
            {contents}
          </h1>
        );
      } else if (level === 2) {
        blocks.push(
          <h2 key={key++} className="mt-2 mb-1 text-[1.18em] font-semibold leading-tight">
            {contents}
          </h2>
        );
      } else {
        blocks.push(
          <h3 key={key++} className="mt-1.5 mb-0.5 text-[1.05em] font-semibold leading-tight">
            {contents}
          </h3>
        );
      }
      i++;
      continue;
    }

    // Blockquote (run of consecutive `> ` lines)
    if (/^> /.test(line)) {
      const collected: string[] = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        collected.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="my-1.5 border-l-2 border-lavender-200 pl-3 italic text-ink-soft"
        >
          {collected.map((l, j) => (
            <div key={j}>{parseInline(l)}</div>
          ))}
        </blockquote>
      );
      continue;
    }

    // Checkbox list
    if (isCheck(line)) {
      const items: { checked: boolean; content: string; indent: number }[] = [];
      while (i < lines.length && isCheck(lines[i])) {
        const cm = lines[i].match(/^( *)- \[([ xX])\] (.*)$/)!;
        items.push({
          indent: cm[1].length,
          checked: cm[2].toLowerCase() === "x",
          content: cm[3],
        });
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-1 space-y-0.5">
          {items.map((it, j) => (
            <li
              key={j}
              className="flex items-start gap-2"
              style={{ paddingLeft: it.indent ? it.indent * 8 : 0 }}
            >
              <input
                type="checkbox"
                checked={it.checked}
                readOnly
                className="mt-[3px] h-3 w-3 accent-lavender-400"
              />
              <span className={it.checked ? "text-ink-mute line-through" : ""}>
                {parseInline(it.content)}
              </span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (isNumbered(line)) {
      const items: { content: string; indent: number }[] = [];
      while (i < lines.length && isNumbered(lines[i])) {
        const nm = lines[i].match(/^( *)\d+\. (.*)$/)!;
        items.push({ indent: nm[1].length, content: nm[2] });
        i++;
      }
      blocks.push(
        <ol
          key={key++}
          className="my-1 list-decimal space-y-0.5"
          style={{ paddingLeft: 22 }}
        >
          {items.map((it, j) => (
            <li
              key={j}
              style={{ marginLeft: it.indent ? it.indent * 8 : 0 }}
            >
              {parseInline(it.content)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Bullet list
    if (isBullet(line)) {
      const items: { content: string; indent: number }[] = [];
      while (i < lines.length && isBullet(lines[i])) {
        const bm = lines[i].match(/^( *)[-*] (.*)$/)!;
        items.push({ indent: bm[1].length, content: bm[2] });
        i++;
      }
      blocks.push(
        <ul
          key={key++}
          className="my-1 list-disc space-y-0.5"
          style={{ paddingLeft: 22 }}
        >
          {items.map((it, j) => (
            <li
              key={j}
              style={{ marginLeft: it.indent ? it.indent * 8 : 0 }}
            >
              {parseInline(it.content)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Empty line → small vertical spacer
    if (line.trim() === "") {
      blocks.push(<div key={key++} className="h-2" aria-hidden="true" />);
      i++;
      continue;
    }

    // Regular line
    blocks.push(
      <div key={key++} className="leading-relaxed">
        {parseInline(line)}
      </div>
    );
    i++;
  }

  return <>{blocks}</>;
}
